import { useNavigate } from "react-router-dom";

import GeneralLayout from "../components/GeneralLayout";
import WorkspaceUserActions from "../components/WorkspaceUserActions";
import { besideSidebar } from "../layout";
import { useIsDashmoteStaff } from "../hooks/useIsDashmoteStaff";
import { useAdminStatus } from "../features/dashai/hooks/useAdminStatus";
import { railGroups } from "../navigation";

/**
 * Landing page at `/`. Two parts only: the workspace/user quick actions, and the
 * Product Analytics cards — the daily-tracked section. Every other destination
 * lives in the rail (grouped by topic, scrollable), so the homepage stays a
 * focused launchpad rather than a directory of every page.
 */
const AdminHomePage = () => {
  const navigate = useNavigate();
  const isStaff = useIsDashmoteStaff();
  const { isAdmin } = useAdminStatus();

  const productAnalytics =
    railGroups({ isStaff, isAdmin }).find(
      (group) => group.name === "Product Analytics",
    )?.entries ?? [];

  const renderCard = ({ route, icon, label, description }) => {
    const CardIcon = icon;
    return (
      <button
        key={route}
        type="button"
        onClick={() => navigate(route)}
        className="flex items-start gap-3 rounded-xl border border-[#0000000f] bg-white p-4 text-left transition hover:border-[#0e49b5] hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0e49b5]"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0e49b5]/10 text-[#0e49b5]">
          <CardIcon className="h-5 w-5" />
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-[#000000d9]">{label}</span>
          <span className="mt-0.5 block text-sm text-[#00000073]">{description}</span>
        </span>
      </button>
    );
  };

  return (
    <GeneralLayout>
      <div style={besideSidebar} className="min-h-screen bg-white">
        <div className="mx-auto max-w-[1200px] px-8 py-10">
          <h1 className="mb-1 font-[Satoshi] text-[2rem] font-bold text-[#000000d9]">
            Admin portal
          </h1>
          <p className="mb-8 text-[#00000073]">
            Jump to any page, or press ⌘K to search.
          </p>

          {/* Staff-only, matching the rail: a non-staff (demo/test) account
              must not be shown provisioning actions it cannot use. */}
          {isStaff && <WorkspaceUserActions />}

          {productAnalytics.length > 0 && (
            <section className="mt-2">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#00000073]">
                Product Analytics
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {productAnalytics.map((entry) =>
                  renderCard({
                    route: entry.route,
                    icon: entry.icon,
                    label: entry.label,
                    description: entry.description,
                  }),
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </GeneralLayout>
  );
};

export default AdminHomePage;
